import React, { useState, useEffect } from 'react';
import he from 'he';
import { MessageSquare, Star, AlertCircle, Calendar, ChevronDown, RefreshCw } from 'lucide-react';

const FeedbackList = () => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  const API_URL = "/api";
  const ITEMS_PER_PAGE = 10;

  useEffect(() => {
    fetchFeedbacks(1, true);
  }, []);

  const fetchFeedbacks = async (page = 1, isInitial = false) => {
    try {
      if (isInitial) {
        setLoading(true);
        setError('');
      } else {
        setLoadingMore(true);
      }

      const response = await fetch(
        `${API_URL}/kotak-saran?page=${page}&limit=${ITEMS_PER_PAGE}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch feedbacks');
      }
      
      const data = await response.json();
      
      if (isInitial) {
        setFeedbacks(data.data || []);
      } else {
        setFeedbacks(prev => [...prev, ...(data.data || [])]);
      }
      
      setHasMore(data.pagination?.hasMore || false);
      setTotalCount(data.pagination?.total || 0);
      setCurrentPage(page);
      
    } catch (error) {
      console.error('Error fetching feedbacks:', error);
      setError('Gagal memuat feedback');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      fetchFeedbacks(currentPage + 1, false);
    }
  };

  const handleRefresh = () => {
    setCurrentPage(1);
    setFeedbacks([]);
    fetchFeedbacks(1, true);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'kritik':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'saran':
        return <MessageSquare className="w-5 h-5 text-green-500" />;
      case 'apresiasi':
        return <Star className="w-5 h-5 text-yellow-500" />;
      default:
        return <MessageSquare className="w-5 h-5 text-gray-500" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'kritik':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'saran':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'apresiasi':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-8">
        <div className="flex items-center justify-center space-x-2">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <span className="text-gray-600">Memuat feedback...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-8">
        <div className="text-center text-red-600">
          <AlertCircle className="w-12 h-12 mx-auto mb-4" />
          <p className="mb-4">{error}</p>
          <button
            onClick={handleRefresh}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Coba Lagi
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl lg:p-8 p-4">
      {/* Header desktop*/}
      <div className='hidden lg:block mb-6'>

        <div className="flex items-center justify-between">
          <h2 className="lg:text-base text-sm font-bold text-teal-900 flex items-center">
            <MessageSquare className="w-6 h-6 mr-2" />
            Feedback Terbaru
          </h2>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500">
              Menampilkan {feedbacks.length} dari {totalCount} feedback
            </span>
            <button
              onClick={handleRefresh}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Header mobile */}
      <div className='lg:hidden mb-4'>
      <div className="flex items-center justify-between">
          <h2 className="lg:text-base text-sm font-bold text-teal-900 flex items-center">
            <MessageSquare className="w-6 h-6 mr-2" />
            Feedback Terbaru
          </h2>
        </div>
          <div className="flex items-center space-x-2">
            <span className="text-xs text-gray-500">
              Menampilkan {feedbacks.length} dari {totalCount} feedback
            </span>
            <button
              onClick={handleRefresh}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
              title="Refresh"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
      </div>

      {feedbacks.length === 0 ? (
        <div className="text-center py-12">
          <MessageSquare className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500">Belum ada feedback yang diberikan</p>
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {feedbacks.map((item, index) => (
              <div 
                key={`${item._id}-${index}`} 
                className="border border-gray-200 rounded-lg lg:p-4 p-3 hover:bg-gray-50 transition-colors duration-200"
              >
                <div className="flex items-start space-x-3">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                    {getTypeIcon(item.type)}
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getTypeColor(item.type)}`}>
                        {item.type?.toUpperCase()}
                      </span>
                      <div className="lg:block hidden flex items-center text-gray-500 text-sm">
                        <Calendar className="w-4 h-4 mr-1" />
                        {new Date(item.createdAt).toLocaleDateString('id-ID', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })} 
                      </div>
                    </div>
                    <div className="lg:hidden block flex items-center text-gray-500 lg:text-sm text-xs mb-2">
                        <Calendar className="w-4 h-4 mr-1" />
                        {new Date(item.createdAt).toLocaleDateString('id-ID', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    <p className="text-gray-700 lg:text-sm text-xs leading-relaxed">{he.decode(item.feedback)}</p>

                    {item.reply && (
                      <div className="mt-3 p-3 bg-teal-50 border-l-4 border-teal-400 rounded-md">
                        <p className="lg:text-sm text-xs text-teal-700 font-semibold mb-1">Balasan Admin:</p>
                        <p className="text-sm text-gray-700">{item.reply}</p>
                        
                        {item.repliedAt && (
                          <p className="text-xs text-teal-500 mt-1 italic">
                            Dibalas pada: {new Date(item.repliedAt).toLocaleDateString('id-ID', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Load More Button */}
          {hasMore && (
            <div className="text-center mt-6">
              <button
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="inline-flex items-center px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {loadingMore ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Memuat...
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4 mr-2" />
                    Lihat Lebih Banyak
                  </>
                )}
              </button>
            </div>
          )}

          {/* End of List Message */}
          {!hasMore && feedbacks.length > 0 && (
            <div className="text-center mt-6 py-4 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                Semua feedback telah ditampilkan ({totalCount} total)
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FeedbackList;